"use client";

import { iconButtonVariants } from "@autumn/ui/components/general/icon-button";
import { Button, type ButtonProps } from "@autumn/ui/components/ui/button";
import { cn, hasSubmitShortcutModifier } from "@autumn/ui/lib/utils";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";

interface IconCheckboxProps
	extends ButtonProps,
		VariantProps<typeof iconButtonVariants> {
	checked?: boolean;
	onCheckedChange?: (checked: boolean) => void;
	icon?: React.ReactNode;
	iconOrientation?: "left" | "center" | "right";
}

const IconCheckbox = React.forwardRef<HTMLButtonElement, IconCheckboxProps>(
	(
		{
			className,
			variant = "secondary",
			size = "sm",
			hide = false,
			iconOrientation = "center",
			asChild = false,
			checked = false,
			onCheckedChange,
			icon,
			onClick,
			onKeyDown,
			children,
			...props
		},
		ref,
	) => {
		const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
			onCheckedChange?.(!checked);
			onClick?.(event);
			// Remove focus after click to prevent stuck active state
			event.currentTarget.blur();
		};

		const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
			// Suppress the native button click without toggling, so
			// cmd/ctrl+enter only triggers sheet-level submit shortcuts
			if (event.key === "Enter" && hasSubmitShortcutModifier(event)) {
				event.preventDefault();
			}
			onKeyDown?.(event);
		};

		const renderIcon = (icon: React.ReactNode) => {
			if (!icon) return null;

			if (React.isValidElement<{ className?: string }>(icon)) {
				return React.cloneElement(icon, {
					className: cn(
						size === "sm" ? "size-3" : "size-3.5",
						icon.props.className,
					),
				} as React.HTMLAttributes<HTMLElement>);
			}

			return icon;
		};

		const renderContent = () => {
			switch (iconOrientation) {
				case "left":
					return (
						<>
							{renderIcon(icon)}
							{children}
						</>
					);
				case "right":
					return (
						<>
							{children}
							{renderIcon(icon)}
						</>
					);
				case "center":
					return (
						<>
							{renderIcon(icon)}
							{children}
						</>
					);
				default:
					return (
						<>
							{renderIcon(icon)}
							{children}
						</>
					);
			}
		};

		const iconToMainClass = () => {
			switch (iconOrientation) {
				case "center":
					if (size === "sm") {
						return "!h-6 w-6";
					} else {
						return "!h-7 w-7";
					}
				default:
					return "";
			}
		};

		if (hide) return null;

		return (
			<Button
				ref={ref}
				variant={variant}
				size={size}
				className={cn(
					iconButtonVariants({ iconOrientation }),
					iconToMainClass(),
					"text-tertiary-foreground!",
					checked && "text-foreground! border-primary",
					className,
				)}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				data-state={checked ? "open" : "closed"}
				aria-pressed={checked}
				{...props}
			>
				{renderContent()}
			</Button>
		);
	},
);

IconCheckbox.displayName = "IconCheckbox";

export { IconCheckbox };
